import React from 'react';
import {
  TouchableOpacity,
  Text,
  View,
  StyleSheet,
  ActivityIndicator,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'danger'
  | 'ghost'
  | 'link';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type IconPosition = 'left' | 'right' | 'only';

interface IconButtonProps {
  title?: string;
  onPress: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
  icon?: keyof typeof Ionicons.glyphMap;
  iconPosition?: IconPosition;
  iconSize?: number;
  iconColor?: string;
  fullWidth?: boolean;
  rounded?: boolean;
}

/**
 * Enhanced Button component with comprehensive icon support
 * Based on the existing Button but with more features and icon integration
 */
const IconButton: React.FC<IconButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  style,
  textStyle,
  icon,
  iconPosition = 'left',
  iconSize,
  iconColor,
  fullWidth = false,
  rounded = false,
}) => {
  const { colors } = useTheme();
  const THEME_COLOR = colors.primary;

  // Get size-specific values
  const getSizeStyles = () => {
    switch (size) {
      case 'xs':
        return {
          paddingVertical: 6,
          paddingHorizontal: 12,
          height: 32,
          fontSize: 12,
          iconSize: iconSize || 14,
        };
      case 'sm':
        return {
          paddingVertical: 8,
          paddingHorizontal: 16,
          height: 40,
          fontSize: 14,
          iconSize: iconSize || 16,
        };
      case 'md':
        return {
          paddingVertical: 12,
          paddingHorizontal: 20,
          height: 48,
          fontSize: 16,
          iconSize: iconSize || 18,
        };
      case 'lg':
        return {
          paddingVertical: 16,
          paddingHorizontal: 24,
          height: 56,
          fontSize: 18,
          iconSize: iconSize || 20,
        };
      case 'xl':
        return {
          paddingVertical: 20,
          paddingHorizontal: 28,
          height: 64,
          fontSize: 20,
          iconSize: iconSize || 22,
        };
      default:
        return {
          paddingVertical: 12,
          paddingHorizontal: 20,
          height: 48,
          fontSize: 16,
          iconSize: iconSize || 18,
        };
    }
  };

  // Determine button styles based on variant
  const getButtonStyle = (): ViewStyle[] => {
    const sizeStyles = getSizeStyles();
    const baseStyles: ViewStyle[] = [
      styles.button,
      {
        paddingVertical: sizeStyles.paddingVertical,
        paddingHorizontal: sizeStyles.paddingHorizontal,
        height: sizeStyles.height,
      },
    ];

    if (fullWidth) {
      baseStyles.push(styles.fullWidth);
    }

    if (rounded) {
      baseStyles.push(styles.rounded);
    }

    if (disabled) {
      baseStyles.push(styles.disabledButton);
      return baseStyles;
    }

    switch (variant) {
      case 'secondary':
        baseStyles.push(styles.secondaryButton);
        break;
      case 'outline':
        baseStyles.push(styles.outlineButton, { borderColor: THEME_COLOR });
        break;
      case 'danger':
        baseStyles.push(styles.dangerButton);
        break;
      case 'ghost':
        baseStyles.push(styles.ghostButton);
        break;
      case 'link':
        baseStyles.push(styles.linkButton);
        break;
      case 'primary':
      default:
        baseStyles.push(styles.primaryButton, { backgroundColor: THEME_COLOR });
    }

    return baseStyles;
  };

  // Determine text styles based on variant
  const getTextStyle = (): TextStyle[] => {
    const sizeStyles = getSizeStyles();
    const baseStyles: TextStyle[] = [
      styles.buttonText,
      { fontSize: sizeStyles.fontSize },
    ];

    if (disabled) {
      baseStyles.push(styles.disabledButtonText);
      return baseStyles;
    }

    switch (variant) {
      case 'secondary':
        baseStyles.push(styles.secondaryButtonText);
        break;
      case 'outline':
        baseStyles.push(styles.outlineButtonText, { color: THEME_COLOR });
        break;
      case 'danger':
        baseStyles.push(styles.dangerButtonText);
        break;
      case 'ghost':
        baseStyles.push(styles.ghostButtonText, { color: THEME_COLOR });
        break;
      case 'link':
        baseStyles.push(styles.linkButtonText, { color: THEME_COLOR });
        break;
      case 'primary':
      default:
        baseStyles.push(styles.primaryButtonText);
    }

    return baseStyles;
  };

  const getIconColor = () => {
    if (iconColor) return iconColor;
    if (disabled) return '#9CA3AF';

    switch (variant) {
      case 'secondary':
        return '#1F2937';
      case 'outline':
      case 'ghost':
      case 'link':
        return THEME_COLOR;
      case 'danger':
        return '#FFFFFF';
      case 'primary':
      default:
        return '#FFFFFF';
    }
  };

  const sizeStyles = getSizeStyles();
  const buttonStyles = getButtonStyle();
  const textStyles = getTextStyle();

  const renderContent = () => {
    if (loading) {
      return (
        <ActivityIndicator
          size="small"
          color={
            variant === 'outline' || variant === 'ghost' || variant === 'link'
              ? THEME_COLOR
              : '#FFFFFF'
          }
        />
      );
    }

    if (iconPosition === 'only' && icon) {
      return (
        <Ionicons
          name={icon}
          size={sizeStyles.iconSize}
          color={getIconColor()}
        />
      );
    }

    return (
      <View style={styles.content}>
        {icon && iconPosition === 'left' && (
          <Ionicons
            name={icon}
            size={sizeStyles.iconSize}
            color={getIconColor()}
            style={title ? styles.iconLeft : undefined}
          />
        )}
        {title && <Text style={[textStyles, textStyle]}>{title}</Text>}
        {icon && iconPosition === 'right' && (
          <Ionicons
            name={icon}
            size={sizeStyles.iconSize}
            color={getIconColor()}
            style={title ? styles.iconRight : undefined}
          />
        )}
      </View>
    );
  };

  return (
    <TouchableOpacity
      style={[buttonStyles, style]}
      onPress={onPress}
      disabled={disabled || loading}
      activeOpacity={0.8}
    >
      {renderContent()}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    fontWeight: '600',
  },
  fullWidth: {
    width: '100%',
  },
  rounded: {
    borderRadius: 24,
  },
  iconLeft: {
    marginRight: 8,
  },
  iconRight: {
    marginLeft: 8,
  },
  // Primary button
  primaryButton: {
    backgroundColor: '#7C3AED',
  },
  primaryButtonText: {
    color: '#FFFFFF',
  },
  // Secondary button
  secondaryButton: {
    backgroundColor: '#F3F4F6',
  },
  secondaryButtonText: {
    color: '#1F2937',
  },
  // Outline button
  outlineButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  outlineButtonText: {
    color: '#7C3AED',
  },
  // Danger button
  dangerButton: {
    backgroundColor: '#EF4444',
  },
  dangerButtonText: {
    color: '#FFFFFF',
  },
  // Ghost button
  ghostButton: {
    backgroundColor: 'transparent',
  },
  ghostButtonText: {
    color: '#7C3AED',
  },
  // Link button
  linkButton: {
    backgroundColor: 'transparent',
    paddingVertical: 4,
    paddingHorizontal: 0,
    height: 'auto',
  },
  linkButtonText: {
    color: '#7C3AED',
    textDecorationLine: 'underline',
  },
  // Disabled state
  disabledButton: {
    backgroundColor: '#E5E7EB',
  },
  disabledButtonText: {
    color: '#9CA3AF',
  },
});

export default IconButton;
